import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Platform, View } from 'react-native';
import { WebView } from 'react-native-webview';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styled from 'styled-components/native';

import Button from '../../../components/Button';
import Spacer from '../../../components/Spacer';
import { Caption, H4, P } from '../../../components/Typography';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import {
  awaitDietIdResult,
  createDietIdSession,
  DIET_ID_NOT_AVAILABLE,
  DIET_ID_RESULT_WINDOW_MS,
  isDietIdEnabled,
  isRecentResult,
  syncDietIdResult
} from '../services/dietId.service';

const StyledView = styled.View`
  width: 100%;
`;
const StyledRow = styled.View`
  flex-direction: row;
  align-items: center;
`;
const StyledModalHeader = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background-color: #fff;
`;

/** Diet ID has no return-to-app redirect, so it runs inside our own modal with a Done button. */
const DietIdModal: React.FC<{ url: string; onDone: () => void }> = ({
  url,
  onDone
}) => {
  const insets = useSafeAreaInsets();
  return (
    <Modal
      visible
      animationType='slide'
      presentationStyle='fullScreen'
      onRequestClose={onDone}
    >
      <View
        style={{ flex: 1, backgroundColor: '#fff', paddingTop: insets.top }}
      >
        <StyledModalHeader>
          <H4 style={{ color: Colors.primary }}>Diet assessment</H4>
          <Button size='small' onPress={onDone}>
            Done
          </Button>
        </StyledModalHeader>
        {Platform.OS === 'web' ? (
          <iframe
            src={url}
            title='Diet ID'
            style={{ flex: 1, width: '100%', border: 0 }}
          />
        ) : (
          <WebView
            source={{ uri: url }}
            style={{ flex: 1 }}
            domStorageEnabled
            startInLoadingState
          />
        )}
      </View>
    </Modal>
  );
};

export interface FormDietIdConfig {
  introText?: string;
}

type Phase = 'idle' | 'launching' | 'waiting' | 'unconfirmed' | 'completed';

export const FormDietId = <T extends object>({
  element
}: FormElementProps<T, FormDietIdConfig>) => {
  const { formValues, setFormValue, studyParticipantId } =
    useDynamicFormContext<T, FormDietIdConfig>();
  const key = (element.dataSource as keyof T) || (element.key as keyof T);

  const [enabled, setEnabled] = useState<boolean | null>();
  const [phase, setPhase] = useState<Phase>(
    formValues[key] && formValues[key] !== (DIET_ID_NOT_AVAILABLE as any)
      ? 'completed'
      : 'idle'
  );
  const [error, setError] = useState<string>();
  const [sessionUrl, setSessionUrl] = useState<string>();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadEnabled = useCallback(() => {
    let stale = false;
    setEnabled(undefined);
    isDietIdEnabled(studyParticipantId).then(result => {
      if (!stale) setEnabled(result);
    });
    return () => {
      stale = true;
    };
  }, [studyParticipantId]);

  useEffect(() => loadEnabled(), [loadEnabled]);

  // A required element the participant can't use would leave Next disabled with
  // no explanation, so when the study has Diet ID off it counts as answered.
  useEffect(() => {
    if (
      enabled === false &&
      formValues[key] !== (DIET_ID_NOT_AVAILABLE as any)
    ) {
      setFormValue(key, DIET_ID_NOT_AVAILABLE as any, element);
    } else if (
      enabled === true &&
      formValues[key] === (DIET_ID_NOT_AVAILABLE as any)
    ) {
      setFormValue(key, null as any, element);
    }
  }, [enabled, element, formValues, key, setFormValue]);

  const poll = useCallback(async () => {
    setError(undefined);
    setPhase('waiting');
    try {
      const completedAt = await awaitDietIdResult(
        Date.now() - DIET_ID_RESULT_WINDOW_MS,
        { shouldContinue: () => mounted.current },
        studyParticipantId
      );
      if (!mounted.current) return;

      if (completedAt === null) {
        // Not marked complete: someone who finished cannot be told apart from
        // someone who closed it straight away, and a false completion would
        // overstate compliance and leave a hole in the study's data.
        setPhase('unconfirmed');
        return;
      }
      setFormValue(key, new Date(completedAt).toISOString() as any, element);
      setPhase('completed');
    } catch {
      if (!mounted.current) return;
      // They may well have taken it, so do not send them back to the start.
      setPhase('unconfirmed');
    }
  }, [element, key, setFormValue, studyParticipantId]);

  // Pulls the result straight from Diet ID; waits for the webhook only if that fails.
  const check = useCallback(async () => {
    setError(undefined);
    setPhase('waiting');
    let completedAt: number | null;
    try {
      completedAt = await syncDietIdResult(studyParticipantId);
    } catch {
      if (mounted.current) await poll();
      return;
    }
    if (!mounted.current) return;
    // An older result on this enrollment's profile belongs to an earlier visit.
    if (!isRecentResult(completedAt)) {
      setPhase('unconfirmed');
      return;
    }
    setFormValue(key, new Date(completedAt).toISOString() as any, element);
    setPhase('completed');
  }, [element, key, poll, setFormValue, studyParticipantId]);

  const done = useCallback(() => {
    setSessionUrl(undefined);
    check();
  }, [check]);

  const start = useCallback(async () => {
    setError(undefined);
    setPhase('launching');
    try {
      const url = await createDietIdSession(studyParticipantId);
      if (!mounted.current) return;
      setSessionUrl(url);
    } catch {
      if (!mounted.current) return;
      setError('We could not start the assessment. Please try again.');
      setPhase('idle');
    }
  }, [studyParticipantId]);

  // A required element with nothing on screen reads as a broken Next button.
  if (enabled === undefined) {
    return (
      <StyledView>
        <StyledRow>
          <ActivityIndicator />
          <View style={{ width: 8 }} />
          <Caption>Loading the diet assessment…</Caption>
        </StyledRow>
      </StyledView>
    );
  }

  if (enabled === false) {
    return (
      <StyledView>
        <Caption>
          This assessment is not available for your study right now. You can
          continue.
        </Caption>
      </StyledView>
    );
  }

  if (enabled === null) {
    return (
      <StyledView>
        <Caption>
          We could not load the diet assessment. Check your connection and try
          again.
        </Caption>
        <Spacer size='small' />
        <Button size='small' outlined onPress={loadEnabled}>
          Try again
        </Button>
      </StyledView>
    );
  }

  return (
    <StyledView>
      {sessionUrl ? <DietIdModal url={sessionUrl} onDone={done} /> : null}
      {element.config?.introText ? (
        <>
          <P>{element.config.introText}</P>
          <Spacer size='small' />
        </>
      ) : null}

      {phase === 'completed' ? (
        <Caption>Your diet assessment has been recorded. Thank you.</Caption>
      ) : phase === 'waiting' ? (
        <StyledRow>
          <ActivityIndicator />
          <View style={{ width: 8 }} />
          <Caption>Checking for your results…</Caption>
        </StyledRow>
      ) : phase === 'unconfirmed' ? (
        <>
          <Caption>
            We have not received your results yet. If you finished the
            assessment, check again in a moment. If you did not finish it, you
            can start again.
          </Caption>
          <Spacer size='small' />
          <StyledRow>
            <Button size='small' outlined onPress={check}>
              Check again
            </Button>
            <View style={{ width: 8 }} />
            <Button size='small' outlined onPress={start}>
              Start again
            </Button>
          </StyledRow>
        </>
      ) : (
        <>
          {error ? (
            <>
              <Caption>{error}</Caption>
              <Spacer size='small' />
            </>
          ) : null}
          <Button size='small' disabled={phase === 'launching'} onPress={start}>
            {phase === 'launching' ? 'Opening…' : 'Start assessment'}
          </Button>
        </>
      )}
    </StyledView>
  );
};

export const FormDietIdPlugin: FormElementPlugin<any, FormDietIdConfig> = {
  name: 'Diet ID Assessment',
  type: 'DietIdAssessment',
  element: FormDietId as React.FC<FormElementProps<any, FormDietIdConfig>>
};
